import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  financialRecordsApi,
  categoriesApi,
  bankTransactionsApi,
} from "../../services/api";
import { formatCurrency } from "../../utils/formatters";
import Spinner from "../../components/common/Spinner";
import {
  BankTransaction,
  FinancialRecord,
} from "@akfdev-com/shared/src/models";
import {
  MoneyDirection,
  ReconTypeOption,
  typesForDirection,
  defaultTypeForDirection,
} from "./reconcileTypes";

/**
 * Explain-and-link a bank transaction (Alt 1 inline + Alt 3 full-page).
 *
 * The treasurer first picks a TYPE (reconciliation Req 14, Money-In / Money-Out
 * taxonomy). Two modes follow:
 *   - "item"    → match an outstanding sub-ledger record. Committing calls the
 *                 settlement path `PUT /financial-records/{id}?action=reconcile`
 *                 (service-charge receipt Dr 1900/Cr 1910; bill payment
 *                 Dr 5001/Cr 1900), bounded/split per Req 4.
 *   - "journal" → no sub-ledger item: post a manual journal to a chosen nominal
 *                 against 1900 Bank via `PUT /bank-transactions/{id}` action
 *                 explain-journal (Req 14 "Other / journal").
 *
 * Transfers (bank-to-bank) are listed but disabled until that path exists.
 */

type Direction = MoneyDirection;

interface CategoryOption {
  code: string;
  name: string;
}

function outstandingOf(record: FinancialRecord): number {
  return (record.amount || 0) - (record.amountPaid || 0);
}

function recordDisplayName(record: FinancialRecord): string {
  return (
    record.contactName ||
    record.supplierName ||
    record.unitName ||
    record.reference ||
    record.title ||
    "Untitled record"
  );
}

function itemPostingHint(recordType: "service_charge" | "bill"): string {
  return recordType === "service_charge"
    ? "Dr Bank / Cr Service Charge Debtors"
    : "Dr Trade Creditors / Cr Bank";
}

export interface ExplainTransactionFormProps {
  transaction: BankTransaction;
  buildingId: string;
  variant?: "inline" | "page";
  /** Inline variant only: link to the full-page explain view. */
  fullPageHref?: string;
  currency?: string;
  onReconciled?: () => void;
  onCancel?: () => void;
}

const ExplainTransactionForm: React.FC<ExplainTransactionFormProps> = ({
  transaction,
  buildingId,
  variant = "inline",
  fullPageHref,
  currency = "GBP",
  onReconciled,
  onCancel,
}) => {
  const direction: Direction = transaction.grossValue >= 0 ? "in" : "out";
  const transactionAmount = Math.abs(transaction.grossValue);
  const typeOptions = typesForDirection(direction);

  const [selectedTypeKey, setSelectedTypeKey] = useState(
    defaultTypeForDirection(direction).key,
  );
  const selectedType: ReconTypeOption =
    typeOptions.find((t) => t.key === selectedTypeKey) ??
    defaultTypeForDirection(direction);
  const isJournal = selectedType.mode === "journal";

  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [nominalCode, setNominalCode] = useState("");
  const [journalNote, setJournalNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const changeType = (key: string) => {
    setSelectedTypeKey(key);
    setSelectedId(null);
    setSearch("");
    setNominalCode("");
  };

  // ── Item mode: outstanding sub-ledger records ─────────────────────────────
  const recordType = selectedType.recordType;
  const {
    data: records,
    isLoading,
    error,
  } = useQuery<FinancialRecord[]>({
    queryKey: ["outstanding", buildingId, recordType],
    queryFn: () => financialRecordsApi.getAll(buildingId, { type: recordType }),
    enabled: !!buildingId && selectedType.mode === "item" && !!recordType,
  });

  const outstandingItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (records ?? [])
      .filter((r) => outstandingOf(r) > 0)
      .filter((r) => {
        if (!term) return true;
        return [r.contactName, r.supplierName, r.unitName, r.reference, r.title]
          .filter(Boolean)
          .some((v) => (v as string).toLowerCase().includes(term));
      })
      .sort((a, b) => {
        const aExact = outstandingOf(a) === transactionAmount ? 0 : 1;
        const bExact = outstandingOf(b) === transactionAmount ? 0 : 1;
        if (aExact !== bExact) return aExact - bExact;
        return outstandingOf(b) - outstandingOf(a);
      });
  }, [records, search, transactionAmount]);

  const selected = outstandingItems.find((r) => r.id === selectedId) ?? null;

  const alreadyAllocated = transaction.allocatedAmount || 0;
  const transactionRemaining = transactionAmount - alreadyAllocated;
  const selectedOutstanding = selected ? outstandingOf(selected) : 0;
  const allocated = selected
    ? Math.min(transactionRemaining, selectedOutstanding)
    : 0;
  const remaining = transactionRemaining - allocated;
  const itemRemainingAfter = selectedOutstanding - allocated;

  // ── Journal mode: nominal categories ──────────────────────────────────────
  const { data: rawCategories } = useQuery<Record<string, unknown>[]>({
    queryKey: ["categories", buildingId],
    queryFn: () => categoriesApi.getAll(),
    enabled: !!buildingId && isJournal,
  });
  const categoryOptions: CategoryOption[] = useMemo(() => {
    return (rawCategories ?? [])
      .map((c) => ({
        code: String(c.nominalCode ?? c.code ?? ""),
        name: String(c.name ?? c.categoryName ?? c.nominalCode ?? ""),
      }))
      .filter((c) => c.code && c.code !== "1900");
  }, [rawCategories]);
  const selectedCategory = categoryOptions.find((c) => c.code === nominalCode);
  const journalHint =
    direction === "in" ? "Dr Bank / Cr nominal" : "Dr nominal / Cr Bank";

  const handleMatch = async () => {
    if (!selected || allocated <= 0) return;
    setSubmitting(true);
    try {
      await financialRecordsApi.reconcile(
        selected.id,
        buildingId,
        transaction.id,
        transaction.buildingId,
        allocated,
      );
      toast.success(
        `Reconciled ${formatCurrency(
          allocated / 100,
          currency,
        )} against ${recordDisplayName(selected)}.`,
      );
      onReconciled?.();
    } catch (err) {
      console.error("Reconcile failed:", err);
      toast.error("Could not reconcile this transaction. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePostJournal = async () => {
    if (!nominalCode) return;
    setSubmitting(true);
    try {
      await bankTransactionsApi.explainJournal(transaction.id, buildingId, {
        nominalCode,
        categoryName: selectedCategory?.name,
        description: journalNote || undefined,
      });
      toast.success(
        `Explained ${formatCurrency(transactionAmount / 100, currency)} to ${
          selectedCategory?.name || nominalCode
        }.`,
      );
      onReconciled?.();
    } catch (err) {
      console.error("Journal explain failed:", err);
      toast.error("Could not post this journal. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const amountColor = direction === "in" ? "text-green-700" : "text-red-600";
  const containerClass =
    variant === "page"
      ? "space-y-5"
      : "rounded-lg border border-blue-200 bg-blue-50/60 p-4 space-y-4";

  return (
    <div className={containerClass} data-testid="explain-transaction-form">
      {/* Header: what we're explaining */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Explain this {direction === "in" ? "receipt" : "payment"}
          </p>
          <p className="mt-0.5 text-sm font-medium text-gray-900">
            {transaction.description}
          </p>
        </div>
        <div className={`text-right font-mono font-semibold ${amountColor}`}>
          {formatCurrency(transaction.grossValue / 100, currency)}
        </div>
      </div>

      {/* Step 1: type selector (Req 14 taxonomy, direction-filtered) */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
          Type
        </p>
        <div className="mt-1 flex flex-wrap gap-2">
          {typeOptions.map((t) => {
            const active = t.key === selectedType.key;
            return (
              <button
                key={t.key}
                type="button"
                disabled={!t.enabled}
                title={t.enabled ? undefined : "Coming soon"}
                onClick={() => t.enabled && changeType(t.key)}
                className={`rounded-md px-2.5 py-1 text-xs font-semibold ${
                  active
                    ? "bg-primary-600 text-white"
                    : t.enabled
                    ? "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                    : "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed"
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        <p className="mt-1 text-xs text-gray-500">
          {isJournal ? journalHint : itemPostingHint(recordType!)}
        </p>
      </div>

      {isJournal ? (
        /* ── Journal mode: explain to a nominal ──────────────────────────── */
        <>
          <div>
            <label
              htmlFor={`journal-nominal-${transaction.id}`}
              className="text-xs font-semibold uppercase tracking-wide text-gray-400"
            >
              Post to nominal
            </label>
            <select
              id={`journal-nominal-${transaction.id}`}
              value={nominalCode}
              onChange={(e) => setNominalCode(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="">Choose a category / nominal…</option>
              {categoryOptions.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name} ({c.code})
                </option>
              ))}
            </select>
            <input
              type="text"
              value={journalNote}
              onChange={(e) => setJournalNote(e.target.value)}
              placeholder="Note (optional)"
              className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div className="rounded-md bg-white border border-gray-200 px-3 py-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">
                {direction === "in" ? "Receipt" : "Payment"} amount
              </span>
              <span className="font-mono text-gray-900">
                {formatCurrency(transactionAmount / 100, currency)}
              </span>
            </div>
            <p className="mt-1 text-xs text-gray-400">
              Posts the full amount to{" "}
              {selectedCategory?.name || "the chosen nominal"} against 1900
              Bank.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={!nominalCode || submitting}
              onClick={handlePostJournal}
              className="inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? "Posting…" : "Post to ledger"}
            </button>
            {variant === "inline" && fullPageHref && (
              <Link
                to={fullPageHref}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Open full page
              </Link>
            )}
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            )}
          </div>
        </>
      ) : (
        /* ── Item mode: match an outstanding record ──────────────────────── */
        <>
          <div>
            <label
              htmlFor={`explain-search-${transaction.id}`}
              className="text-xs font-semibold uppercase tracking-wide text-gray-400"
            >
              Allocate against an open{" "}
              {direction === "in" ? "service charge" : "bill"}
            </label>
            <input
              id={`explain-search-${transaction.id}`}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={
                direction === "in"
                  ? "Search by resident, unit or reference"
                  : "Search by supplier or reference"
              }
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />

            <div className="mt-2 max-h-56 overflow-y-auto rounded-md border border-gray-200 bg-white">
              {isLoading ? (
                <div className="p-4">
                  <Spinner />
                </div>
              ) : error ? (
                <p className="p-4 text-sm text-red-600">
                  Failed to load outstanding items.
                </p>
              ) : outstandingItems.length === 0 ? (
                <p className="p-4 text-sm text-gray-500">
                  No open {direction === "in" ? "service charges" : "bills"} to
                  match.
                </p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {outstandingItems.map((item) => {
                    const bal = outstandingOf(item);
                    const isExact = bal === transactionAmount;
                    const isSelected = item.id === selectedId;
                    return (
                      <li key={item.id}>
                        <button
                          type="button"
                          onClick={() => setSelectedId(item.id)}
                          className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                            isSelected ? "bg-primary-50" : ""
                          }`}
                          aria-pressed={isSelected}
                        >
                          <span className="min-w-0">
                            <span className="block truncate font-medium text-gray-900">
                              {recordDisplayName(item)}
                            </span>
                            <span className="block truncate text-xs text-gray-500">
                              {[item.reference, item.date]
                                .filter(Boolean)
                                .join(" \u00b7 ")}
                            </span>
                          </span>
                          <span className="ml-3 shrink-0 text-right">
                            <span className="block font-mono text-gray-900">
                              {formatCurrency(bal / 100, currency)} due
                            </span>
                            {isExact && (
                              <span className="block text-xs font-semibold text-green-700">
                                exact match
                              </span>
                            )}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          {/* Allocation summary */}
          <div className="rounded-md bg-white border border-gray-200 px-3 py-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">
                {direction === "in" ? "Receipt" : "Payment"} amount
              </span>
              <span className="font-mono text-gray-900">
                {formatCurrency(transactionAmount / 100, currency)}
              </span>
            </div>
            {alreadyAllocated > 0 && (
              <div className="mt-1 flex justify-between">
                <span className="text-gray-500">Already allocated</span>
                <span className="font-mono text-gray-900">
                  {formatCurrency(alreadyAllocated / 100, currency)}
                </span>
              </div>
            )}
            <div className="mt-1 flex justify-between">
              <span className="text-gray-500">Allocated to this item</span>
              <span className="font-mono text-gray-900">
                {formatCurrency(allocated / 100, currency)}
              </span>
            </div>
            {selected && (
              <div className="mt-1 text-xs text-gray-400">
                Item outstanding{" "}
                {formatCurrency(selectedOutstanding / 100, currency)}
                {" \u2192 "}
                {formatCurrency(itemRemainingAfter / 100, currency)} after
              </div>
            )}
            <div className="mt-2 flex justify-between border-t border-gray-100 pt-2">
              <span className="font-semibold text-gray-900">
                Remaining to explain
              </span>
              <span
                className={`font-mono font-semibold ${
                  selected && remaining === 0
                    ? "text-green-700"
                    : "text-gray-900"
                }`}
              >
                {formatCurrency(remaining / 100, currency)}
              </span>
            </div>
            {selected && remaining > 0 && (
              <p className="mt-2 text-xs text-amber-700">
                This {direction === "in" ? "receipt" : "payment"} is larger than
                the selected item. {formatCurrency(remaining / 100, currency)}{" "}
                will remain to explain — match it to another item afterwards.
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={!selected || allocated <= 0 || submitting}
              onClick={handleMatch}
              className="inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? "Reconciling…" : "Match & reconcile"}
            </button>
            {variant === "inline" && fullPageHref && (
              <Link
                to={fullPageHref}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Open full page
              </Link>
            )}
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ExplainTransactionForm;
